import { Navigation, Plus, ChevronUp, ChevronDown, Trash2, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CustomInput from '../../../../../../../components/forms/CustomInput';
import CustomSelect from '../../../../../../../components/forms/CustomSelect';
import CustomDatePicker from '../../../../../../../components/forms/CustomDatePicker';
import Clock from '../../../../../../../components/forms/Clock';
import vouchersData from '../../../../../../../data/vouchers/vouchersData.json';

const travelModes = vouchersData.travelModes || [];

const TravelBookingsSection = ({
  formData,
  setFormData,
  addTravel,
  moveTravel,
  handleArrayFieldChange
}) => {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 md:p-8 shadow-sm" id="travel-bookings-section">
      <div className="flex items-center justify-between gap-2.5 sm:gap-4 mb-4 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-7 h-7 sm:w-10 sm:h-10 shrink-0 bg-teal-50 text-primary rounded-lg sm:rounded-xl flex items-center justify-center shadow-inner">
            <Navigation size={14} className="sm:w-5 sm:h-5" />
          </div>
          <div>
            <h3 className="text-xs sm:text-base font-semibold sm:font-bold text-slate-800 leading-tight">Travel Bookings</h3>
            <p className="hidden sm:block text-xs text-slate-500 font-normal sm:font-medium mt-0.5">Add flights, trains, or other transportation</p>
          </div>
        </div>
        <button
          onClick={addTravel}
          className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1 sm:px-4 sm:py-2 bg-primary text-white rounded-lg sm:rounded-xl font-medium sm:font-semibold text-[11px] sm:text-xs hover:opacity-95 transition-all active:scale-95 shadow-sm shrink-0 cursor-pointer"
          type="button"
        >
          <Plus size={12} className="sm:w-3.5 sm:h-3.5" />
          <span>
            <span className="sm:hidden">Add</span>
            <span className="hidden sm:inline">Add Travel</span>
          </span>
        </button>
      </div>

      <div className="space-y-6">
        <AnimatePresence mode="popLayout">
          {formData.travel_bookings?.map((travel, idx) => (
            <motion.div
              layout
              initial={{ opacity: 0, scale: 0.98, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.1 } }}
              transition={{ type: "spring", stiffness: 350, damping: 25 }}
              key={travel._id || travel.id || `travel-${idx}`}
              id={`travel-${travel._id || travel.id || idx}`}
              className="p-4 sm:p-6 bg-white border border-slate-200 rounded-3xl shadow-sm relative group hover:shadow-md hover:border-slate-300 transition-all mb-4"
            >
              <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 mb-4 pb-3 border-b border-slate-100">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-black tracking-wider uppercase shrink-0">
                  Travel Entry #{idx + 1}
                </span>
                <div className="flex items-center gap-1.5 shrink-0 ml-auto">
                  <div className="flex items-center gap-1 bg-slate-50 p-1 rounded-xl border border-slate-100">
                    <button
                      onClick={() => moveTravel(idx, 'up')}
                      disabled={idx === 0}
                      className="p-1.5 hover:bg-white rounded-lg text-slate-600 disabled:opacity-30 transition-all shadow-sm disabled:shadow-none cursor-pointer disabled:cursor-not-allowed"
                      type="button"
                      title="Move Up"
                    >
                      <ChevronUp size={14} />
                    </button>
                    <button
                      onClick={() => moveTravel(idx, 'down')}
                      disabled={idx === formData.travel_bookings.length - 1}
                      className="p-1.5 hover:bg-white rounded-lg text-slate-600 disabled:opacity-30 transition-all shadow-sm disabled:shadow-none cursor-pointer disabled:cursor-not-allowed"
                      type="button"
                      title="Move Down"
                    >
                      <ChevronDown size={14} />
                    </button>
                  </div>
                  <button
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      travel_bookings: prev.travel_bookings.filter((_, i) => i !== idx)
                    }))}
                    className="flex items-center gap-1.5 px-2.5 sm:px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors font-bold text-xs shadow-sm shrink-0 cursor-pointer"
                    title="Remove Travel"
                    type="button"
                  >
                    <Trash2 size={14} /> <span className="hidden sm:inline">Remove</span>
                  </button>
                </div>
              </div>

              {/* 1 Line - Operator Name | Mode of Transport | Flight Number */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-3.5 mt-2">
                <div>
                  <CustomInput
                    label="Operator name *"
                    value={travel.operator || ''}
                    onChange={(e) => handleArrayFieldChange('travel_bookings', idx, 'operator', e.target.value)}
                    placeholder="Operator / Airline / Agency"
                  />
                </div>
                <div>
                    <CustomSelect
                      label="Mode of transport *"
                      value={travel.mode_of_transport || ''}
                      onChange={(val) => handleArrayFieldChange('travel_bookings', idx, 'mode_of_transport', val)}
                      options={travelModes}
                      placeholder="Select Mode"
                    />
                </div>
                <div>
                  <CustomInput
                    label={
                      travel.mode_of_transport === 'flight' ? 'Flight Number' :
                        travel.mode_of_transport === 'train' ? 'Train Number' :
                          travel.mode_of_transport === 'bus' ? 'Bus Number' :
                            travel.mode_of_transport === 'waterways' ? 'Vessel / Cruise Name' : 'Vehicle Number'
                    }
                    mono
                    value={travel.vehicle_number || ''}
                    onChange={(e) => handleArrayFieldChange('travel_bookings', idx, 'vehicle_number', e.target.value)}
                    placeholder={travel.mode_of_transport === 'flight' ? 'e.g. 6E-2041' : travel.mode_of_transport === 'train' ? 'e.g. 12951' : 'e.g. GA-01-A-1234'}
                  />
                </div>
              </div>

              {/* 2 Line - Departure Destination | Terminal for Flight | Arrival Destinations | Terminal For Flight */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-3.5">
                <div>
                  <CustomInput
                    label="Departure Destination *"
                    value={travel.from || ''}
                    onChange={(e) => handleArrayFieldChange('travel_bookings', idx, 'from', e.target.value)}
                    placeholder="Origin City / Terminal"
                  />
                </div>
                <div>
                  <CustomInput
                    label={travel.mode_of_transport === 'flight' ? 'Departure Terminal' : 'Dep. Terminal / Platform'}
                    value={travel.departure_terminal || ''}
                    onChange={(e) => handleArrayFieldChange('travel_bookings', idx, 'departure_terminal', e.target.value)}
                    placeholder="e.g. T2"
                  />
                </div>
                <div>
                  <CustomInput
                    label="Arrival Destination *"
                    value={travel.to || ''}
                    onChange={(e) => handleArrayFieldChange('travel_bookings', idx, 'to', e.target.value)}
                    placeholder="Destination City / Terminal"
                  />
                </div>
                <div>
                  <CustomInput
                    label={travel.mode_of_transport === 'flight' ? 'Arrival Terminal' : 'Arr. Terminal / Platform'}
                    value={travel.arrival_terminal || ''}
                    onChange={(e) => handleArrayFieldChange('travel_bookings', idx, 'arrival_terminal', e.target.value)}
                    placeholder="e.g. T3"
                  />
                </div>
              </div>

              {/* 3 Line - Departure Date | Departure Time | Arrival Date | Arrival Time */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-3.5">
                <div>
                  <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">
                    Departure Date <span className="text-rose-500 ml-0.5">*</span>
                  </label>
                  <CustomDatePicker
                    value={travel.journey_date}
                    onChange={(val) => handleArrayFieldChange('travel_bookings', idx, 'journey_date', val)}
                    leftIcon={<Calendar size={14} />}
                    inputClassName="border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary rounded-xl h-[42px] px-3.5 text-xs font-semibold text-slate-800 transition-all outline-none shadow-sm"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">Departure Time</label>
                  <Clock
                    value={travel.departure_time || ''}
                    onChange={(val) => handleArrayFieldChange('travel_bookings', idx, 'departure_time', val)}
                    placeholder="00:00"
                    inputClassName="h-[42px] px-3 rounded-xl text-xs font-semibold text-slate-800 outline-none shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">Arrival Date</label>
                  <CustomDatePicker
                    value={travel.arrival_date}
                    onChange={(val) => handleArrayFieldChange('travel_bookings', idx, 'arrival_date', val)}
                    leftIcon={<Calendar size={14} />}
                    inputClassName="border-slate-200 focus:border-primary focus:ring-1 focus:ring-primary rounded-xl h-[42px] px-3.5 text-xs font-semibold text-slate-800 transition-all outline-none shadow-sm"
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 tracking-wide mb-1.5">Arrival Time</label>
                  <Clock
                    value={travel.arrival_time || ''}
                    onChange={(val) => handleArrayFieldChange('travel_bookings', idx, 'arrival_time', val)}
                    placeholder="00:00"
                    inputClassName="h-[42px] px-3 rounded-xl text-xs font-semibold text-slate-800 outline-none shadow-sm"
                  />
                </div>
              </div>

              {/* 4 Line - PNR | Travel Class | Seat Numbers */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3.5">
                <div>
                  <CustomInput
                    label={travel.mode_of_transport === 'waterways' ? 'Ticket / PNR No.' : 'PNR'}
                    mono
                    value={travel.pnr || ''}
                    onChange={(e) => handleArrayFieldChange('travel_bookings', idx, 'pnr', e.target.value)}
                    placeholder="PNR-XXXX"
                  />
                </div>
                <div>
                  <CustomInput
                    label="Travel Class"
                    value={travel.travel_class || ''}
                    onChange={(e) => handleArrayFieldChange('travel_bookings', idx, 'travel_class', e.target.value)}
                    placeholder="e.g. Economy / Business / 3AC / Sleeper"
                  />
                </div>
                <div>
                  <CustomInput
                    label={travel.mode_of_transport === 'waterways' ? 'Seat / Cabin No.' : 'Seat Number(s)'}
                    mono
                    value={travel.seat_no || ''}
                    onChange={(e) => handleArrayFieldChange('travel_bookings', idx, 'seat_no', e.target.value)}
                    placeholder="e.g. 12A, 12B or S1-24"
                  />
                </div>
              </div>

              {/* 5 Line - Passenger Names */}
              <div className="mb-3.5">
                <CustomInput
                  label="Passenger Names"
                  value={travel.passenger_name || ''}
                  onChange={(e) => handleArrayFieldChange('travel_bookings', idx, 'passenger_name', e.target.value)}
                  placeholder="Comma separated passenger names"
                />
              </div>

              {['bus', 'cab'].includes(travel.mode_of_transport) && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <CustomInput
                      label="Driver name"
                      value={travel.driver_name || ''}
                      onChange={(e) => handleArrayFieldChange('travel_bookings', idx, 'driver_name', e.target.value)}
                      placeholder="Driver Name"
                    />
                  </div>
                  <div>
                    <CustomInput
                      label="Driver contact"
                      mono
                      value={travel.driver_contact || ''}
                      onChange={(e) => handleArrayFieldChange('travel_bookings', idx, 'driver_contact', e.target.value)}
                      placeholder="Driver Contact No."
                    />
                  </div>
                </div>
              )}

              {/* Custom Fields Section for Travel */}
              <div className="border-t border-slate-100 pt-4 mt-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <label className="text-xs font-bold text-slate-700">
                    Custom Fields
                  </label>
                  <button
                    onClick={() => {
                      const currentFields = travel.custom_fields || [];
                      handleArrayFieldChange('travel_bookings', idx, 'custom_fields', [
                        ...currentFields,
                        { label: '', value: '' }
                      ]);
                    }}
                    className="text-xs text-primary font-bold hover:underline flex items-center gap-1 shrink-0 cursor-pointer"
                    type="button"
                  >
                    <Plus size={12} /> Add Field
                  </button>
                </div>
                {(!travel.custom_fields || travel.custom_fields.length === 0) ? (
                  <p className="text-xs text-slate-400 italic ml-1">No custom fields added yet.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {travel.custom_fields.map((field, fIdx) => (
                      <div key={fIdx} className="flex flex-col sm:flex-row items-stretch sm:items-end gap-2 group/field bg-slate-50/50 p-3 sm:p-0 rounded-2xl sm:rounded-none border sm:border-0 border-slate-100">
                        <div className="flex-1 min-w-0">
                          <CustomInput
                            label="Label"
                            placeholder="e.g. Flight Type"
                            value={field.label || ''}
                            onChange={(e) => {
                              const updatedFields = [...(travel.custom_fields || [])];
                              updatedFields[fIdx] = { ...updatedFields[fIdx], label: e.target.value };
                              handleArrayFieldChange('travel_bookings', idx, 'custom_fields', updatedFields);
                            }}
                            inputClassName="h-[38px] text-xs font-semibold px-3.5"
                          />
                        </div>
                        <div className="flex-[2] min-w-0">
                          <CustomInput
                            label="Value"
                            placeholder="e.g. Layover Info"
                            value={field.value || ''}
                            onChange={(e) => {
                              const updatedFields = [...(travel.custom_fields || [])];
                              updatedFields[fIdx] = { ...updatedFields[fIdx], value: e.target.value };
                              handleArrayFieldChange('travel_bookings', idx, 'custom_fields', updatedFields);
                            }}
                            inputClassName="h-[38px] text-xs font-semibold px-3.5"
                          />
                        </div>
                        <button
                          onClick={() => {
                            const updatedFields = (travel.custom_fields || []).filter((_, fi) => fi !== fIdx);
                            handleArrayFieldChange('travel_bookings', idx, 'custom_fields', updatedFields);
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-xl transition-colors mb-0.5 border border-transparent hover:border-red-100 self-end sm:self-auto shrink-0 cursor-pointer"
                          type="button"
                          title="Delete Field"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default TravelBookingsSection;
